import type { CodeWorldSlug } from '@vrlearning/lesson-schema';
import type { Layer, SoundPlan, Wobble } from './plan.js';

/**
 * Every world's sound (design, "Per-world soundscapes"): synthesised where
 * code can do it -- wind, surf, rumble, under water, bubbles, a room's air,
 * a clock, a burner, sails, lapping -- and the approved Freesound recordings
 * where it cannot. Levels are the plan's starting values; Pieter tunes them
 * on a Quest. Space ahead is −z, right is +x (lesson-schema's WORLDS).
 * sound.test.ts holds every plan to the budgets.
 */

const GUSTS: Wobble = { rates: [0.07, 0.19], depth: 3 };
const STRONG_GUSTS: Wobble = { rates: [0.06, 0.17], depth: 5 };
/** A bed from three points round the stage, turned so the copies drift apart. */
const ROUND: Layer['emitters'] = [
  [-18, 4, -10],
  [18, 4, -10],
  [0, 4, 20],
];
const DRIFT = [1, 0.97, 1.02];

const wind = (level: number, gusts: Wobble = GUSTS, lowpass = 1000): Layer => ({
  name: 'wind',
  source: {
    kind: 'noise',
    colour: 'pink',
    shape: [{ type: 'lowpass', frequency: lowpass }],
    filter: { type: 'bandpass', frequency: 520, q: 0.5 },
    sweep: { rates: [0.05, 0.13], depth: 0.45 },
    gusts,
  },
  level,
  emitters: ROUND,
  rates: DRIFT,
});

/** The volcano is 260 m off: heard 0.76 s after it is seen. */
const VOLCANO_DELAY = 0.76;

export const ISLAND_SOUND: SoundPlan = {
  layers: [
    wind(-10),
    {
      name: 'surf',
      source: { kind: 'events', event: 'waves', seconds: 24 },
      level: -12,
      emitters: [
        [-25, -6, -40],
        [0, -8, -55],
        [28, -6, -45],
      ],
      rates: DRIFT,
    },
    {
      name: 'gulls',
      source: { kind: 'recording', id: 'gulls-loop.ogg' },
      level: -14,
      emitters: [
        [-30, 15, -45],
        [35, 18, -35],
      ],
      hrtf: true,
    },
    {
      name: 'rumble',
      source: { kind: 'noise', colour: 'brown', shape: [{ type: 'lowpass', frequency: 160 }] },
      level: -14,
      emitters: [{ feature: 'vulkaan' }],
      hrtf: true,
      follows: {
        feature: 'vulkaan',
        level: { rustig: -20, rokend: -14, uitbarsting: -8 },
        seconds: { uitbarsting: 25 },
        delay: VOLCANO_DELAY,
      },
    },
    {
      // The Quest's small speakers carry 150–400 Hz, not the rumble's bass.
      name: 'roar',
      source: {
        kind: 'noise',
        colour: 'brown',
        shape: [
          { type: 'highpass', frequency: 150 },
          { type: 'lowpass', frequency: 400 },
        ],
        gusts: { rates: [0.11, 0.29], depth: 4 },
      },
      level: -10,
      emitters: [{ feature: 'vulkaan' }],
      hrtf: true,
      follows: {
        feature: 'vulkaan',
        level: { rustig: 'off', rokend: 'off', uitbarsting: -10 },
        seconds: { uitbarsting: 25 },
        delay: VOLCANO_DELAY,
      },
    },
    {
      // Pieter's Quest test (29 Sep): an eruption is heard in bangs and pops,
      // not only a roar. Deep distant booms 1.5–6 s apart and, between them,
      // the lava bombs' crackles 9 dB under them; each its own level (±3 dB)
      // and pitch (±12 %). Peaks at most −18 dBFS at master 0 dB (sound.test.ts).
      name: 'eruption',
      source: {
        kind: 'bursts',
        booms: [1.5, 6],
        pops: [0.35, 1.4],
        popDb: -9,
        spread: { db: 3, pitch: 0.12 },
      },
      level: -9,
      emitters: [{ feature: 'vulkaan' }],
      hrtf: true,
      follows: {
        feature: 'vulkaan',
        level: { rustig: 'off', rokend: 'off', uitbarsting: -9 },
        seconds: { uitbarsting: 25 },
        delay: VOLCANO_DELAY,
      },
    },
    {
      name: 'ash',
      source: { kind: 'noise', colour: 'white', shape: [{ type: 'highpass', frequency: 3500 }] },
      level: -20,
      emitters: [],
      follows: {
        feature: 'vulkaan',
        level: { rustig: 'off', rokend: 'off', uitbarsting: -20 },
        seconds: { uitbarsting: 25 },
        delay: VOLCANO_DELAY,
      },
    },
  ],
};

export const LAB_SOUND: SoundPlan = {
  layers: [
    {
      name: 'ventilation',
      source: { kind: 'noise', colour: 'pink', shape: [{ type: 'lowpass', frequency: 400 }] },
      level: -18,
      emitters: [
        [-6, 4, -6],
        [6, 4, 6],
      ],
      rates: DRIFT,
    },
    {
      name: 'clock',
      source: { kind: 'events', event: 'tick', seconds: 1 },
      level: -24,
      // The wall clock (the science lab package's): ahead, 3.25 m up.
      emitters: [[0, 3.25, -9.88]],
      hrtf: true,
    },
    {
      name: 'burner',
      source: {
        kind: 'noise',
        colour: 'white',
        shape: [{ type: 'bandpass', frequency: 1800, q: 0.8 }],
      },
      level: -20,
      emitters: [{ feature: 'bunsenbrander' }],
      hrtf: true,
      follows: { feature: 'bunsenbrander', level: { aan: -20, uit: 'off' }, seconds: 1 },
    },
  ],
};

export const FOREST_SOUND: SoundPlan = {
  layers: [
    {
      name: 'leaves',
      source: {
        kind: 'noise',
        colour: 'pink',
        shape: [
          { type: 'highpass', frequency: 2000 },
          { type: 'lowpass', frequency: 6000 },
        ],
        gusts: { rates: [0.06, 0.15], depth: 4 },
      },
      level: -14,
      emitters: [
        [-15, 8, -12],
        [16, 9, -8],
        [0, 10, 18],
      ],
      rates: DRIFT,
      follows: { feature: 'seizoen', level: { zomer: -14, herfst: -9 } },
    },
    {
      name: 'birds',
      source: { kind: 'recording', id: 'forest-birds-loop.ogg' },
      level: -8,
      emitters: [
        [-12, 9, -14],
        [14, 10, -10],
        [-4, 11, 16],
      ],
      rates: DRIFT,
      follows: { feature: 'seizoen', level: { zomer: -8, herfst: -17 } },
    },
  ],
};

export const MEADOW_SOUND: SoundPlan = {
  // The butterflies (vlinders) are silent: no layer follows them, on purpose.
  layers: [
    {
      name: 'meadow',
      source: { kind: 'recording', id: 'summer-meadow-loop.ogg' },
      level: -8,
      emitters: [
        [-10, 1, -8],
        [10, 1, 6],
      ],
      rates: [1, 0.97],
    },
    {
      ...wind(-18, GUSTS, 900),
      emitters: [
        [-15, 3, -10],
        [15, 3, 10],
      ],
    },
  ],
};

export const POLDER_SOUND: SoundPlan = {
  layers: [
    wind(-10, STRONG_GUSTS),
    {
      name: 'larks',
      source: { kind: 'recording', id: 'polder-larks-loop.ogg' },
      level: -10,
      // Over the fields, right of the dike.
      emitters: [
        [25, 12, -20],
        [30, 14, 15],
      ],
      rates: [1, 0.97],
    },
    {
      // Against the dike on the left; closer and higher when the water is high.
      name: 'lapping',
      source: { kind: 'events', event: 'lapping', seconds: 16 },
      level: -16,
      emitters: [
        [-12, -1.5, -10],
        [-12, -1.5, 12],
      ],
      rates: [1, 0.97],
      follows: {
        feature: 'water',
        level: { laag: -16, hoog: -9 },
        shift: { laag: [0, 0, 0], hoog: [3, 1.2, 0] },
      },
    },
    {
      name: 'mill',
      source: { kind: 'recording', id: 'windmill-loop.ogg' },
      level: -14,
      emitters: [{ feature: 'molen', offset: [0, -6, 0] }],
      hrtf: true,
      follows: { feature: 'molen', level: { stil: 'off', draaien: -14 }, seconds: 4 },
    },
    {
      // Four sails a turn; a turn takes 5.71 s at full speed (windmill.ts: 1.1 rad/s).
      name: 'sails',
      source: { kind: 'events', event: 'swish', seconds: (2 * Math.PI) / 1.1, count: 4 },
      level: -18,
      emitters: [{ feature: 'molen' }],
      hrtf: true,
      follows: { feature: 'molen', level: { stil: 'off', draaien: -18 }, seconds: 4 },
    },
  ],
};

/** The waterline at low tide (coast.ts, the code beach's shore: 30.4 m ahead, 1.5 m down); high tide 13.5 m up the beach. */
export const BEACH_SOUND: SoundPlan = {
  layers: [
    {
      ...wind(-12),
      emitters: [
        [-18, 4, -10],
        [18, 4, 10],
      ],
    },
    {
      name: 'surf',
      source: { kind: 'events', event: 'waves', seconds: 24 },
      level: -6,
      emitters: [
        [-18, -1.5, -30.4],
        [0, -1.5, -30.4],
        [18, -1.5, -30.4],
      ],
      rates: DRIFT,
      follows: {
        feature: 'getij',
        level: { eb: -6, vloed: -3 },
        shift: { eb: [0, 0, 0], vloed: [0, 0.95, 13.5] },
      },
    },
    {
      name: 'gulls',
      source: { kind: 'recording', id: 'gulls-loop.ogg' },
      level: -12,
      emitters: [
        [-20, 14, -28],
        [24, 16, -20],
      ],
      hrtf: true,
    },
  ],
};

/**
 * The villa (ADR-037): the beach's recordings heard through the villa's glass -- the wind 20 dB
 * down, the surf 60 m ahead and 7.5 m below 12 dB down, the gulls 18 dB down -- and no tide.
 */
export const VILLA_SOUND: SoundPlan = {
  layers: [
    {
      ...wind(-20),
      emitters: [
        [-18, 4, -10],
        [18, 4, 10],
      ],
    },
    {
      name: 'surf',
      source: { kind: 'events', event: 'waves', seconds: 24 },
      level: -12,
      emitters: [
        [-30, -7.5, -60],
        [0, -7.5, -60],
        [30, -7.5, -60],
      ],
      rates: DRIFT,
    },
    {
      name: 'gulls',
      source: { kind: 'recording', id: 'gulls-loop.ogg' },
      level: -18,
      emitters: [
        [-24, 10, -40],
        [26, 12, -34],
      ],
      hrtf: true,
    },
  ],
};

export const SEA_FLOOR_SOUND: SoundPlan = {
  layers: [
    {
      name: 'underwater',
      source: {
        kind: 'noise',
        colour: 'brown',
        shape: [{ type: 'lowpass', frequency: 400 }],
        filter: { type: 'lowpass', frequency: 400 },
        sweep: { rates: [0.03], depth: 0.35 },
      },
      level: -8,
      emitters: [
        [-10, 3, -8],
        [10, 3, 8],
      ],
      rates: [1, 0.97],
    },
    {
      name: 'bubbles',
      source: { kind: 'events', event: 'bubbles', seconds: 12 },
      level: -16,
      // From the kelp and the rocks.
      emitters: [
        [-7, 0.5, -6],
        [8, 0.5, -9],
        [3, 0.5, 9],
      ],
      rates: DRIFT,
    },
    {
      name: 'school',
      source: {
        kind: 'noise',
        colour: 'pink',
        shape: [{ type: 'bandpass', frequency: 700, q: 0.7 }],
        gusts: { rates: [0.21, 0.37], depth: 4 },
      },
      level: -20,
      emitters: [{ feature: 'vissen' }],
      follows: { feature: 'vissen', level: { zwemmen: -20, weg: 'off' } },
    },
  ],
};

export const TOWN_SQUARE_SOUND: SoundPlan = {
  layers: [
    {
      name: 'town',
      source: { kind: 'noise', colour: 'brown', shape: [{ type: 'lowpass', frequency: 250 }] },
      level: -18,
      emitters: [
        [-40, 5, -40],
        [40, 5, 30],
      ],
      rates: [1, 0.97],
    },
    {
      name: 'pigeons',
      source: { kind: 'recording', id: 'pigeons-loop.ogg' },
      level: -16,
      emitters: [
        [-4, 0.3, -7],
        [6, 0.3, 5],
      ],
      rates: [1, 0.97],
    },
    {
      // At the stalls (townsquare.ts: ahead left, and the right-hand row).
      name: 'market',
      source: { kind: 'recording', id: 'market-loop.ogg' },
      // −10 rather than the design's −9: its sum with the hum and the pigeons stays within 6 dB.
      level: -10,
      emitters: [
        [-10, 1.5, -13.5],
        [-3, 1.5, -13.5],
        [13, 1.5, -2],
      ],
      hrtf: true,
      rates: DRIFT,
      follows: { feature: 'markt', level: { leeg: 'off', markt: -10 }, seconds: 3 },
    },
  ],
};

export const STREET_SOUND: SoundPlan = {
  layers: [
    {
      name: 'street',
      source: { kind: 'recording', id: 'amsterdam-street-loop.ogg' },
      level: -10,
      emitters: [
        [-20, 2, -18],
        [20, 2, -18],
      ],
      rates: [1, 0.97],
      follows: { feature: 'verkeer', level: { rustig: -10, druk: -5 } },
    },
    {
      name: 'tyres',
      source: {
        kind: 'noise',
        colour: 'white',
        shape: [{ type: 'bandpass', frequency: 3000, q: 0.7 }],
        gusts: { rates: [0.23, 0.41], depth: 6 },
      },
      level: -20,
      emitters: [
        [-10, 0.3, -12],
        [10, 0.3, -12],
      ],
      rates: [1, 0.97],
    },
  ],
};

export const MUSEUM_SOUND: SoundPlan = {
  layers: [
    {
      name: 'hall',
      source: { kind: 'noise', colour: 'pink', shape: [{ type: 'lowpass', frequency: 300 }] },
      level: -18,
      emitters: [
        [-8, 4, -8],
        [8, 4, 8],
      ],
      rates: [1, 0.97],
      follows: { feature: 'licht', level: { dag: -18, avond: -22 } },
    },
    {
      name: 'visitors',
      source: { kind: 'recording', id: 'museum-hall-loop.ogg' },
      level: -12,
      // The far ends of the hall.
      emitters: [
        [0, 2, -20],
        [0, 2, 18],
      ],
      rates: [1, 0.97],
      follows: { feature: 'licht', level: { dag: -12, avond: 'off' }, seconds: 3 },
    },
  ],
};

export const SOUNDSCAPES: Readonly<Record<CodeWorldSlug, SoundPlan>> = {
  'volcanic-island': ISLAND_SOUND,
  'science-lab': LAB_SOUND,
  forest: FOREST_SOUND,
  meadow: MEADOW_SOUND,
  polder: POLDER_SOUND,
  beach: BEACH_SOUND,
  'sea-floor': SEA_FLOOR_SOUND,
  'town-square': TOWN_SQUARE_SOUND,
  'modern-street': STREET_SOUND,
  'museum-hall': MUSEUM_SOUND,
  villa: VILLA_SOUND,
};
